import { useState } from "react";
import axios from "axios";
import { useAsync } from "react-use";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import Tile from "../../../Components/Tiles/Tile";
import TileContent from "../../../Components/Tiles/TileContent";
import OrdersNavigation from "Navigation/OrdersNavigation";
import useUser from "../../../system/useUser";
import { AssessmentKpiGranularity, AssessmentKpiRange, AssessmentKpiTimeSeriesResult, UserRole } from "../../../system/Domain";
import AssessmentKpiTimeSeriesChart from "./AssessmentKpiTimeSeriesChart";

const granularityOptions: { value: AssessmentKpiGranularity; label: string }[] = [
	{ value: "Day", label: "Tag" },
	{ value: "Week", label: "Woche" },
	{ value: "Month", label: "Monat" }
];

const rangeOptions: { value: AssessmentKpiRange; label: string }[] = [
	{ value: "Last7Days", label: "Letzte 7 Tage" },
	{ value: "Last30Days", label: "Letzte 30 Tage" },
	{ value: "LastYear", label: "Letzte 365 Tage" },
	{ value: "RunningWeek", label: "Laufende Woche" },
	{ value: "RunningMonth", label: "Laufender Monat" },
	{ value: "RunningYear", label: "Laufendes Jahr" }
];

const AssessmentKpiTimeSeriesLayout = () => {
	const [user, , hasRole] = useUser();

	const isAldUser = hasRole(UserRole.AldUser);
	const canSeeDekra = isAldUser || user?.company?.name === "Dekra";
	const canSeeMacadam = isAldUser || user?.company?.name === "Macadam";

	const [granularity, setGranularity] = useState<AssessmentKpiGranularity>("Day");
	const [range, setRange] = useState<AssessmentKpiRange>("RunningMonth");

	const timeSeries = useAsync(async () => {
		const { data } = await axios.get<AssessmentKpiTimeSeriesResult>("/api/orders/assessments/kpis/timeseries", {
			params: { granularity, range }
		});
		return data;
	}, [granularity, range]);

	return (
		<Layout
			title="Gutachten Kpis"
			navigation={<OrdersNavigation />}
		>
			<Box mb={3} display="flex" flexWrap="wrap" gap={3} alignItems="center">
				<Box>
					<Typography variant="caption" display="block" gutterBottom>
						Granularität
					</Typography>
					<ToggleButtonGroup
						value={granularity}
						exclusive
						size="small"
						onChange={(_, value) => value && setGranularity(value)}
					>
						{granularityOptions.map((o) => (
							<ToggleButton key={o.value} value={o.value}>
								{o.label}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>
				<Box>
					<Typography variant="caption" display="block" gutterBottom>
						Zeitraum
					</Typography>
					<ToggleButtonGroup
						value={range}
						exclusive
						size="small"
						onChange={(_, value) => value && setRange(value)}
					>
						{rangeOptions.map((o) => (
							<ToggleButton key={o.value} value={o.value}>
								{o.label}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>
			</Box>

			{!canSeeDekra && !canSeeMacadam && "Nicht autorisiert"}

			<Box display="flex" flexDirection="column" gap={3}>
				{canSeeDekra && (
					<Tile title={timeSeries.value?.dekra.name || "Gutachtenaufträge Dekra"}>
						<TileContent>
							<AssessmentKpiTimeSeriesChart group={timeSeries.value?.dekra} loading={timeSeries.loading} />
						</TileContent>
					</Tile>
				)}
				{canSeeMacadam && (
					<Tile title={timeSeries.value?.macadam.name || "Gutachtenaufträge Macadam"}>
						<TileContent>
							<AssessmentKpiTimeSeriesChart group={timeSeries.value?.macadam} loading={timeSeries.loading} />
						</TileContent>
					</Tile>
				)}
			</Box>
		</Layout>
	);
};

export default AssessmentKpiTimeSeriesLayout;
